import React from 'react'
import { AlertTriangle, CreditCard, Crown, Zap } from 'lucide-react'
import { getPlanDisplayName, formatPrice, calculatePlanPrice } from '../lib/billing'
import { Button } from './ui/button'

interface TrialExpiredModalProps {
  isOpen: boolean
  memberCount: number
  onUpgrade: (planName: string) => void
  upgrading: string | null
}

export const TrialExpiredModal: React.FC<TrialExpiredModalProps> = ({
  isOpen,
  memberCount,
  onUpgrade,
  upgrading
}) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="m-4 w-full max-w-md border border-border bg-background p-6 shadow-lg">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          
          <h3 className="mb-2 text-lg font-semibold text-foreground">
            Your Trial Has Expired
          </h3>
          
          <p className="mb-6 text-sm text-muted-foreground">
            Your 7-day free trial has ended. Please choose a plan to continue using SyncSimple.
          </p>

          <div className="space-y-4 mb-6">
            {/* Essentials Plan */}
            <div className="border border-border p-4 text-left">
              <div className="flex items-center space-x-3 mb-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Crown className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground">{getPlanDisplayName('essentials')}</h4>
                  <p className="text-sm text-muted-foreground">
                    {formatPrice(1.50)}/month
                  </p>
                </div>
              </div>
              
              <div className="mb-3 text-sm text-muted-foreground">
                • Up to 4 team members<br/>
                • AI-powered daily summaries<br/>
                • Priority support
              </div>

              <Button
                onClick={() => onUpgrade('essentials')}
                disabled={upgrading === 'essentials'}
                className="w-full"
              >
                {upgrading === 'essentials' ? 'Processing...' : 'Choose Essentials'}
              </Button>
            </div>

            {/* Advanced Plan */}
            <div className="border border-border p-4 text-left">
              <div className="flex items-center space-x-3 mb-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Zap className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground">{getPlanDisplayName('advanced')}</h4>
                  <p className="text-sm text-muted-foreground">
                    {formatPrice(calculatePlanPrice('advanced', memberCount))}/user/month
                  </p>
                </div>
              </div>
              
              <div className="mb-3 text-sm text-muted-foreground">
                • Unlimited team members<br/>
                • Advanced analytics & insights<br/>
                • Premium support
              </div>

              <Button
                onClick={() => onUpgrade('advanced')}
                disabled={upgrading === 'advanced'}
                className="w-full"
              >
                {upgrading === 'advanced' ? 'Processing...' : 'Choose Advanced'}
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-center text-sm text-muted-foreground">
            <CreditCard className="h-4 w-4 mr-2" />
            <span>Secure payment powered by Stripe</span>
          </div>
        </div>
      </div>
    </div>
  )
}
